import { useCallback, useEffect, useState } from 'react';
import { Calendar, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingSpinner } from '@/components/ui/loading';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { UserRole } from '@/types/api';
import {
  deleteScheduledReport,
  getScheduledReports,
  runScheduledReport,
  type ScheduledReportDto,
} from '@/features/reports/api/reports-api';
import { ScheduleReportModal } from '@/features/reports/components/schedule-report-modal';
import { ScheduledReportRow } from './scheduled-report-row';
import { ScheduledReportCard } from './scheduled-report-card';

const PER_PAGE = 25;

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; schedules: ScheduledReportDto[]; totalCount: number; totalPages: number }
  | { status: 'error'; message: string };

interface RowStatus {
  busy: boolean;
  message?: string;
  variant?: 'success' | 'error';
}

interface PropertyScheduledReportsTabProps {
  propertyId: string;
  propertyName: string;
  userEmail?: string;
}

export function PropertyScheduledReportsTab({
  propertyId,
  propertyName,
  userEmail,
}: PropertyScheduledReportsTabProps) {
  const activeRole = useActiveRole();
  const canRunNow = activeRole === UserRole.Owner;

  const [page, setPage] = useState(1);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduledReportDto | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<ScheduledReportDto | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  // Per-row transient status for run-now / cancel error feedback. Keyed by
  // schedule id so concurrent actions on different rows don't trample each
  // other's messages.
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  // Single load helper used by the propertyId/page effect and by manual
  // refetches after mutations. The cancelled flag prevents an in-flight
  // request from racing a newer one if propertyId/page changes mid-fetch.
  const refetch = useCallback(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getScheduledReports({ propertyId, page, perPage: PER_PAGE })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          schedules: res.items,
          totalCount: res.totalCount,
          totalPages: res.totalPages,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to load scheduled reports.';
        setState({ status: 'error', message });
      });
    return () => { cancelled = true; };
  }, [propertyId, page]);

  useEffect(() => refetch(), [refetch]);

  function setRow(id: string, next: RowStatus | undefined) {
    setRowStatus((prev) => {
      const copy = { ...prev };
      if (!next) delete copy[id];
      else copy[id] = next;
      return copy;
    });
  }

  async function handleRunNow(schedule: ScheduledReportDto) {
    setRow(schedule.id, { busy: true });
    try {
      await runScheduledReport(schedule.id);
      setRow(schedule.id, {
        busy: false,
        message: 'Triggered — report will be emailed shortly.',
        variant: 'success',
      });
      // Backend updates LastRunAt + NextRunAt synchronously inside RunNowAsync,
      // so refetching after the call returns picks up the fresh timestamps.
      refetch();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to trigger report.';
      setRow(schedule.id, { busy: false, message, variant: 'error' });
    }
  }

  async function handleConfirmCancel() {
    if (!confirmCancel) return;
    const target = confirmCancel;
    setCancelLoading(true);
    try {
      await deleteScheduledReport(target.id);
      setConfirmCancel(null);
      setCancelLoading(false);
      refetch();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to cancel schedule.';
      setRow(target.id, { busy: false, message, variant: 'error' });
      setConfirmCancel(null);
      setCancelLoading(false);
    }
  }

  function handleSaved() {
    setCreateOpen(false);
    setEditing(null);
    refetch();
  }

  const schedules = state.status === 'ready' ? state.schedules : [];
  const totalCount = state.status === 'ready' ? state.totalCount : 0;
  const totalPages = state.status === 'ready' ? state.totalPages : 1;
  const isEmpty = state.status === 'ready' && schedules.length === 0;

  return (
    <div className="flex-1 flex flex-col gap-4">
      {/* Header — title + primary CTA. The title uses the same hierarchy
          as section titles elsewhere in the portal. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            Scheduled Reports for {propertyName}
          </h2>
          {state.status === 'ready' && totalCount > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="tabular-nums">{totalCount}</span>{' '}
              {totalCount === 1 ? 'schedule' : 'schedules'}
            </p>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setCreateOpen(true)}
          data-testid="scheduled-reports-add-button"
        >
          <Plus className="w-3.5 h-3.5" />
          Schedule a Report
        </Button>
      </div>

      {state.status === 'loading' ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center min-h-[240px]">
          <LoadingSpinner size="lg" />
        </Card>
      ) : state.status === 'error' ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={<RefreshCw className="w-12 h-12" />}
            title="Couldn't load scheduled reports"
            description={state.message}
            action={{
              label: 'Try again',
              onClick: () => { refetch(); },
              icon: <RefreshCw className="w-4 h-4" />,
            }}
          />
        </Card>
      ) : isEmpty ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={<Calendar className="w-12 h-12" />}
            title="No scheduled reports yet"
            description="Get a recurring snapshot of usage delivered straight to your inbox."
            action={{
              label: 'Schedule a Report',
              onClick: () => setCreateOpen(true),
              icon: <Plus className="w-4 h-4" />,
            }}
          />
        </Card>
      ) : (
        <>
          {/* Desktop table — hidden on narrow screens where it would
              overflow horizontally. */}
          <Card variant="elevated" padding="none" className="hidden md:block">
            <Table density="compact">
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Delivery email</TableHead>
                  <TableHead>Runs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => {
                  const status = rowStatus[schedule.id];
                  return (
                    <ScheduledReportRow
                      key={schedule.id}
                      schedule={schedule}
                      canRunNow={canRunNow}
                      isBusy={status?.busy ?? false}
                      statusMessage={status?.message}
                      statusMessageVariant={status?.variant}
                      onEdit={(s) => setEditing(s)}
                      onCancel={(s) => setConfirmCancel(s)}
                      onRunNow={handleRunNow}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards — same data, stacked. The table breaks down on
              narrow widths so a card layout is friendlier. */}
          <div className="md:hidden flex flex-col gap-3">
            {schedules.map((schedule) => {
              const status = rowStatus[schedule.id];
              return (
                <ScheduledReportCard
                  key={schedule.id}
                  schedule={schedule}
                  canRunNow={canRunNow}
                  isBusy={status?.busy ?? false}
                  statusMessage={status?.message}
                  statusMessageVariant={status?.variant}
                  onEdit={(s) => setEditing(s)}
                  onCancel={(s) => setConfirmCancel(s)}
                  onRunNow={handleRunNow}
                />
              );
            })}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={setPage}
              perPage={PER_PAGE}
              itemNoun={{ singular: 'schedule', plural: 'schedules' }}
            />
          )}
        </>
      )}

      {/* Create modal */}
      <ScheduleReportModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        propertyId={propertyId}
        propertyName={propertyName}
        userEmail={userEmail}
        onSaved={handleSaved}
      />

      {/* Edit modal — keyed on schedule id so internal useState defaults
          re-initialise from the editing prop each time a different row is
          opened (Modal already unmounts/remounts when isOpen flips false→
          true, but switching directly between rows would not). */}
      {editing && (
        <ScheduleReportModal
          key={editing.id}
          isOpen={!!editing}
          onClose={() => setEditing(null)}
          propertyId={propertyId}
          propertyName={propertyName}
          userEmail={userEmail}
          editingSchedule={editing}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmCancel}
        title="Cancel scheduled report"
        description="Are you sure you want to cancel this schedule? It will stop running."
        confirmLabel="Cancel schedule"
        cancelLabel="Keep schedule"
        onConfirm={handleConfirmCancel}
        onCancel={() => setConfirmCancel(null)}
        isLoading={cancelLoading}
      />
    </div>
  );
}
